import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import MobileModal from './MobileModal';
import { ALL_BOAT_CLASSES } from '../constants/boatClasses';
import { getAuthHeaders } from '../utils/api';
import { PrognosticBadges, PrognosticTableCells, PROGNOSTIC_HEADERS } from './PrognosticMetrics';
import { ResultComparisonBadges } from './ResultComparison';
import { buildResultComparisons } from '../utils/resultComparisons';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const RESULT_TYPES = [
  { value: 'workout', label: 'Workout', color: 'bg-blue-100 text-blue-800' },
  { value: 'test', label: 'Test', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'race', label: 'Race', color: 'bg-green-100 text-green-800' },
  { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-800' }
];

// Format seconds to MM:SS.s
function formatTime(totalSeconds) {
  if (!totalSeconds) return 'N/A';
  // Round to 1 decimal place first to avoid floating point issues
  const rounded = Math.round(totalSeconds * 10) / 10;
  const minutes = Math.floor(rounded / 60);
  const secs = rounded % 60;
  // Format seconds with exactly 1 decimal place
  const secsFormatted = secs.toFixed(1);
  const [secsInt, secsDec] = secsFormatted.split('.');
  return `${minutes}:${secsInt.padStart(2, '0')}.${secsDec}`;
}

// Format date from MM/DD/YYYY to readable format
function formatDate(dateStr) {
  if (!dateStr) return '';
  const [month, day, year] = dateStr.split('/');
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// NumberInput component that prevents wheel events
function NumberInput({ className, ...props }) {
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handleWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      element.blur();
    };

    element.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      element.removeEventListener('wheel', handleWheel);
    };
  }, []);

  return (
    <input
      ref={ref}
      type="number"
      className={className}
      {...props}
    />
  );
}

// DecimalInput component for seconds - uses text to avoid floating point display issues
function DecimalInput({ className, value, onChange, placeholder, ...props }) {
  const handleChange = (e) => {
    const val = e.target.value;
    // Allow empty, digits, and one decimal point
    if (val === '' || /^\d*\.?\d*$/.test(val)) {
      onChange(e);
    }
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      className={className}
      {...props}
    />
  );
}

export default function AthleteResults({ urlTemplateId, onTemplateHandled }) {
  const { token, user, isAdmin, isCoach, canEnterTeamResults } = useAuth();
  const [results, setResults] = useState([]);
  // Previous-result deltas and best markers, per result id.
  const resultComparisons = useMemo(() => buildResultComparisons(results), [results]);
  const [athletes, setAthletes] = useState([]);
  const [myAthleteId, setMyAthleteId] = useState(null); // Current user's athlete ID
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingResult, setEditingResult] = useState(null);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('all'); // 'all' for coaches/admins, 'my' for athletes

  // Form state
  const [selectedAthletes, setSelectedAthletes] = useState([]); // Multi-select for new, single for edit
  const [athleteSearch, setAthleteSearch] = useState(''); // Search input for athlete selection
  const [resultDate, setResultDate] = useState(new Date().toISOString().split('T')[0]);
  const [distanceMeters, setDistanceMeters] = useState('');
  const [timeMinutes, setTimeMinutes] = useState('');
  const [timeSeconds, setTimeSeconds] = useState('');
  const [strokeRate, setStrokeRate] = useState('');
  const [boatClass, setBoatClass] = useState('');
  const [notes, setNotes] = useState('');
  const [resultType, setResultType] = useState('workout');
  const [intervalCount, setIntervalCount] = useState(1);
  const [intervalRestMinutes, setIntervalRestMinutes] = useState('');
  const [intervalRestSeconds, setIntervalRestSeconds] = useState('');
  const [intervalDetails, setIntervalDetails] = useState([]); // Array of {time_seconds, stroke_rate, distance_meters?}
  const [intervalSecondsInputs, setIntervalSecondsInputs] = useState({}); // Track raw input strings for interval seconds while typing
  const [intervalStrokeRateInputs, setIntervalStrokeRateInputs] = useState({}); // Track raw input strings for interval stroke rate while typing
  const [workoutMode, setWorkoutMode] = useState('distance'); // 'distance' (fixed distance, record time) or 'time' (fixed time, record distance)
  const [fixedTimeMinutes, setFixedTimeMinutes] = useState(''); // For time-based mode: fixed time per piece
  const [fixedTimeSeconds, setFixedTimeSeconds] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Filter state
  const [filterAthlete, setFilterAthlete] = useState('');
  
  // Report building state
  const [selectedResultIds, setSelectedResultIds] = useState([]);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportTitle, setReportTitle] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [filterBoatClass, setFilterBoatClass] = useState('');
  const [filterResultType, setFilterResultType] = useState('');

  // GMS state
  const [gmsStandards, setGmsStandards] = useState({});
  const [gmsBoatClasses, setGmsBoatClasses] = useState([]);

  // Templates state (for athletes)
  const [pendingTemplates, setPendingTemplates] = useState([]);
  const [submittedTemplates, setSubmittedTemplates] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null); // Template being submitted
  
  // Team results state
  const [showTeamResults, setShowTeamResults] = useState(false);
  const [teamResultsData, setTeamResultsData] = useState(null);
  const [teamResultsLoading, setTeamResultsLoading] = useState(false);
  const [expandedTeamResult, setExpandedTeamResult] = useState(null); // ID of expanded result
  
  // Results page tab: 'myResults' or 'teamResults'
  const [resultsTab, setResultsTab] = useState('myResults');

  // Normalize boat class to handle legacy formats (Men's/Women's → M/W)
  const normalizeBoatClass = (boatClass) => {
    if (!boatClass) return boatClass;
    return boatClass
      .replace(/^Men's\s*/i, 'M ')
      .replace(/^Women's\s*/i, 'W ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const canViewAll = canEnterTeamResults();
  const canMutateResult = (result) => {
    if (isCoach() || isAdmin()) return true;
    if (result.created_by === user?.id) return true;
    const myAthleteId = user?.athleteId || user?.athlete_id;
    if (myAthleteId && parseInt(result.athlete_id, 10) === parseInt(myAthleteId, 10)) return true;
    if (result.user_id === user?.id) return true;
    return false;
  };

  useEffect(() => {
    if (canViewAll) {
      fetchAthletes();
    }
    fetchResults();
    fetchGmsStandards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterAthlete, filterBoatClass, filterResultType, viewMode, canViewAll]);

  // Set initial view mode based on user role
  useEffect(() => {
    if (!canViewAll) {
      setViewMode('my');
    }
  }, [canViewAll]);

  // Fetch pending templates for all users (athletes always, coaches if they have athlete profile)
  useEffect(() => {
    fetchPendingTemplates();
  }, [token]);

  // Handle template from URL (email link)
  useEffect(() => {
    const handleUrlTemplate = async () => {
      if (!urlTemplateId) return;
      
      // First check if template is in pending list
      const template = pendingTemplates.find(t => t.id === urlTemplateId);
      if (template) {
        console.log('[AthleteResults] Auto-opening template from URL:', template.name);
        openFormFromTemplate(template);
        if (onTemplateHandled) onTemplateHandled();
        return;
      }
      
      // If not in pending, fetch the specific template to see why
      try {
        console.log('[AthleteResults] Template not in pending list, fetching directly:', urlTemplateId);
        const response = await fetch(`${API_URL}/api/result-templates/${urlTemplateId}`, {
          headers: getAuthHeaders(token)
        });
        if (response.ok) {
          const templateData = await response.json();
          console.log('[AthleteResults] Fetched template:', templateData);
          
          if (templateData.has_submitted) {
            setError('You have already submitted a result for this template.');
          } else if (!templateData.is_active) {
            setError('This result request is no longer active.');
          } else {
            // Template exists and user can submit - open the form
            openFormFromTemplate(templateData);
          }
        } else if (response.status === 404) {
          setError('This result request was not found or you do not have access to it.');
        } else {
          console.error('[AthleteResults] Failed to fetch template:', response.status);
        }
      } catch (err) {
        console.error('[AthleteResults] Error fetching template:', err);
      }
      
      if (onTemplateHandled) onTemplateHandled();
    };
    
    handleUrlTemplate();
  }, [urlTemplateId, pendingTemplates, token]);

  const fetchPendingTemplates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/result-templates`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        const now = new Date();
        // Filter to only pending (not submitted), active templates that haven't passed their due date
        setPendingTemplates(data.filter(t => {
          if (t.has_submitted || !t.is_active) return false;
          // If there's a due date, check if it has passed
          if (t.due_date) {
            const dueDate = new Date(t.due_date);
            if (dueDate < now) return false;
          }
          return true;
        }));
        // Also get submitted templates (active ones where user has submitted)
        setSubmittedTemplates(data.filter(t => t.has_submitted && t.is_active));
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const fetchTeamResults = async (templateId) => {
    try {
      setTeamResultsLoading(true);
      const response = await fetch(`${API_URL}/api/result-templates/${templateId}/team-results`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setTeamResultsData(data);
        setShowTeamResults(true);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load team results');
      }
    } catch (err) {
      console.error('Error fetching team results:', err);
      setError('Failed to load team results');
    } finally {
      setTeamResultsLoading(false);
    }
  };

  const fetchAthletes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/athletes`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        const roster = Array.isArray(data) ? data : [];
        if (user?.role === 'coxswain' && user?.team) {
          setAthletes(roster.filter((a) => a.team === user.team || (Array.isArray(a.teams) && a.teams.includes(user.team))));
        } else {
          setAthletes(roster);
        }
      }
    } catch (err) {
      console.error('Error fetching athletes:', err);
    }
  };

  const fetchGmsStandards = async () => {
    try {
      const response = await fetch(`${API_URL}/api/gms`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        console.log('[GMS] Raw standards from API:', data.standards);
        // Convert to lookup map: { boatClass: { time_2k_seconds, pace_500m } }
        const standardsMap = {};
        (data.standards || []).forEach(s => {
          // Store under normalized key for consistent lookup
          const normalizedKey = normalizeBoatClass(s.boat_class);
          console.log('[GMS] Normalizing:', s.boat_class, '→', normalizedKey);
          standardsMap[normalizedKey] = {
            time_2k_seconds: parseFloat(s.time_2k_seconds),
            pace_500m: parseFloat(s.pace_500m)
          };
        });
        console.log('[GMS] Final standards map keys:', Object.keys(standardsMap));
        setGmsStandards(standardsMap);
        setGmsBoatClasses([...new Set((data.standards || []).map(s => normalizeBoatClass(s.boat_class)).filter(Boolean))]);
      }
    } catch (err) {
      console.error('Error fetching GMS standards:', err);
    }
  };

  const boatClassOptions = [...new Set([
    ...ALL_BOAT_CLASSES.map((bc) => bc.value),
    ...gmsBoatClasses
  ])].sort();

  // Calculate % GMS for a result using pace comparison
  // % GMS = (GMS pace / Athlete pace) * 100
  // Uses TOTAL time and TOTAL distance to calculate average pace per 500m
  const calculatePercentGMS = (result) => {
    const normalizedBoatClass = normalizeBoatClass(result.boat_class);
    const gms = gmsStandards[normalizedBoatClass];
    
    // Debug logging - can remove later
    if (!gms) {
      console.log('[GMS Calc] No GMS found for:', result.boat_class, '→', normalizedBoatClass, 
                  '| Available keys:', Object.keys(gmsStandards));
    }
    
    if (!gms || !result.total_time_seconds || !result.distance_meters) return null;
    
    const gmsPace = gms.pace_500m; // GMS pace per 500m (2k time / 4)
    
    // Calculate athlete's average pace per 500m using total time and total distance
    // This works for both single pieces and intervals
    const athletePace = (result.total_time_seconds / result.distance_meters) * 500;
    
    // % GMS = (GMS pace / Athlete pace) * 100
    const pct = Math.round((gmsPace / athletePace) * 1000) / 10;
    console.log('[GMS Calc] Result:', result.id, '| Boat:', normalizedBoatClass, 
                '| Time:', result.total_time_seconds, '| Dist:', result.distance_meters,
                '| AthletePace:', athletePace.toFixed(2), '| GMSPace:', gmsPace, '| %:', pct);
    return pct;
  };

  const fetchResults = async () => {
    try {
      let url;
      const params = new URLSearchParams();

      if (viewMode === 'my' || !canViewAll) {
        // Fetch own results
        url = `${API_URL}/api/athlete-results/my-results`;
      } else {
        // Fetch all results (coaches/admins)
        url = `${API_URL}/api/athlete-results`;
        if (filterAthlete) params.append('athleteId', filterAthlete);
      }

      if (filterBoatClass) params.append('boatClass', filterBoatClass);
      if (filterResultType) params.append('resultType', filterResultType);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: getAuthHeaders(token)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('[Results] Fetched', data.results?.length, 'results');
        // Log template results specifically
        const templateResults = (data.results || []).filter(r => r.template_id);
        if (templateResults.length > 0) {
          console.log('[Results] Template results:', templateResults.length, templateResults.map(r => ({
            id: r.id, template_id: r.template_id, boat_class: r.boat_class, distance_meters: r.distance_meters
          })));
        }
        setResults(data.results || []);
        if (data.athleteId) {
          setMyAthleteId(data.athleteId);
        }
      } else {
        console.error('Failed to fetch results');
      }
    } catch (err) {
      console.error('Error fetching results:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // For athletes adding their own results, auto-select themselves
    const athletesToSubmit = canViewAll ? selectedAthletes : (myAthleteId ? [myAthleteId.toString()] : []);

    // For distance mode, require distance. For time mode, distance comes from intervals.
    if (athletesToSubmit.length === 0 || !resultDate || !boatClass) {
      setError('Please fill in all required fields');
      return;
    }

    if (workoutMode === 'distance' && !distanceMeters) {
      setError('Please enter distance for distance-based workout');
      return;
    }

    if (workoutMode === 'time' && !fixedTimeMinutes) {
      setError('Please enter time per piece for time-based workout');
      return;
    }

    // Build interval details and total time based on workout mode
    let finalIntervalDetails = null;
    let totalTimeSeconds = null;

    if (intervalCount > 1) {
      if (workoutMode === 'distance') {
        // Distance-based: require individual interval times
        if (!intervalDetails || intervalDetails.length !== intervalCount) {
          setError('Please enter time for all intervals');
          return;
        }
        const validIntervals = intervalDetails.filter(i => i && i.time_seconds != null && i.time_seconds > 0);
        if (validIntervals.length !== intervalCount) {
          setError('Please enter time for all intervals');
          return;
        }
        
        // Check if any intervals have variable distances (for pyramid workouts)
        const hasVariableDistances = intervalDetails.some(i => i.distance_meters && i.distance_meters > 0);
        const defaultDistance = distanceMeters ? parseInt(distanceMeters) : null;
        
        finalIntervalDetails = validIntervals.map((i, idx) => ({
          time_seconds: parseFloat(i.time_seconds),
          stroke_rate: i.stroke_rate ? parseFloat(i.stroke_rate) : null,
          // Preserve variable distances if they exist, otherwise null (uniform distance from form)
          distance_meters: hasVariableDistances 
            ? (intervalDetails[idx]?.distance_meters || defaultDistance || null)
            : null
        }));
        totalTimeSeconds = finalIntervalDetails.reduce((sum, i) => sum + i.time_seconds, 0);
      } else {
        // Time-based: require individual interval distances
        if (!intervalDetails || intervalDetails.length !== intervalCount) {
          setError('Please enter distance for all intervals');
          return;
        }
        const validIntervals = intervalDetails.filter(i => i && i.distance_meters != null && i.distance_meters > 0);
        if (validIntervals.length !== intervalCount) {
          setError('Please enter distance for all intervals');
          return;
        }
        
        // Check if template has variable intervals (different times per interval)
        const templateIntervals = activeTemplate?.intervals;
        const hasVariableIntervals = templateIntervals && Array.isArray(templateIntervals) && templateIntervals.length === intervalCount;
        
        // Default fixed time (used for uniform intervals)
        const fixedTimeSecs = parseInt(fixedTimeMinutes || 0) * 60 + parseFloat(fixedTimeSeconds || 0);
        
        finalIntervalDetails = validIntervals.map((i, idx) => {
          // Priority for interval time:
          // 1. Template's per-interval time (for new submissions from template)
          // 2. Existing interval time from intervalDetails (for edits)
          // 3. Fall back to fixed time
          let intervalTime;
          if (hasVariableIntervals && templateIntervals[idx]?.time_seconds) {
            intervalTime = Number(templateIntervals[idx].time_seconds);
          } else if (i.time_seconds && i.time_seconds > 0) {
            // When editing, preserve the original interval times
            intervalTime = Number(i.time_seconds);
          } else {
            intervalTime = fixedTimeSecs;
          }
          
          return {
            time_seconds: intervalTime,
            stroke_rate: i.stroke_rate ? parseFloat(i.stroke_rate) : null,
            distance_meters: parseInt(i.distance_meters)
          };
        });
        
        // Sum up the individual interval times for the total
        totalTimeSeconds = finalIntervalDetails.reduce((sum, i) => sum + i.time_seconds, 0);
      }
    } else {
      // Single interval
      if (workoutMode === 'time') {
        // Time-based single: need fixed time and distance
        if (!distanceMeters) {
          setError('Please enter distance achieved');
          return;
        }
        totalTimeSeconds = parseInt(fixedTimeMinutes || 0) * 60 + parseFloat(fixedTimeSeconds || 0);
      } else {
        // Distance-based single: use total time
        if (!timeMinutes && timeMinutes !== '0') {
          setError('Please enter total time');
          return;
        }
        const mins = parseInt(timeMinutes || 0);
        const secs = parseFloat(timeSeconds || 0);
        totalTimeSeconds = mins * 60 + secs;
      }
      
      // Validate that we have a valid time
      if (isNaN(totalTimeSeconds) || totalTimeSeconds <= 0) {
        setError('Please enter a valid total time');
        return;
      }
    }

    // Convert date from YYYY-MM-DD to MM/DD/YYYY
    const [year, month, day] = resultDate.split('-');
    const formattedDate = `${month}/${day}/${year}`;

    // Parse interval rest from minutes and seconds to total seconds
    const intervalRestSecondsValue = (intervalRestMinutes || intervalRestSeconds) 
      ? (parseInt(intervalRestMinutes || 0) * 60 + parseFloat(intervalRestSeconds || 0))
      : null;

    setSubmitting(true);

    try {
      if (editingResult) {
        // Editing: single athlete update
        const response = await fetch(`${API_URL}/api/athlete-results/${editingResult.id}`, {
          method: 'PUT',
        headers: getAuthHeaders(token),
          body: JSON.stringify({
            athleteId: parseInt(athletesToSubmit[0]),
            resultDate: formattedDate,
            distanceMeters: distanceMeters ? parseInt(distanceMeters) : null,
            totalTimeSeconds: totalTimeSeconds || 0,
            avgStrokeRate: strokeRate ? parseFloat(strokeRate) : null,
            boatClass,
            notes: notes || null,
            resultType,
            intervalCount: parseInt(intervalCount) || 1,
            intervalRestSeconds: intervalRestSecondsValue,
            intervalDetails: finalIntervalDetails,
            workoutMode
          })
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to update result');
        }
      } else {
        // Creating: support multiple athletes
        const results = [];
        const errors = [];

        for (const athleteId of athletesToSubmit) {
          try {
            const response = await fetch(`${API_URL}/api/athlete-results`, {
              method: 'POST',
        headers: getAuthHeaders(token),
              body: JSON.stringify({
                athleteId: parseInt(athleteId),
                resultDate: formattedDate,
                distanceMeters: distanceMeters ? parseInt(distanceMeters) : null,
                totalTimeSeconds: totalTimeSeconds || 0,
                avgStrokeRate: strokeRate ? parseFloat(strokeRate) : null,
                boatClass,
                notes: notes || null,
                resultType,
                intervalCount: parseInt(intervalCount) || 1,
                intervalRestSeconds: intervalRestSecondsValue,
                intervalDetails: finalIntervalDetails,
                workoutMode,
                templateId: activeTemplate?.id || null
              })
            });

            if (response.ok) {
              results.push(athleteId);
            } else {
              let errorMessage = 'Failed to create athlete result';
              try {
                const data = await response.json();
                errorMessage = data.error || data.message || errorMessage;
              } catch {
                errorMessage = `HTTP ${response.status}: ${response.statusText}`;
              }
              const athlete = athletes.find(a => a.id === parseInt(athleteId));
              errors.push(`${athlete?.name || athleteId}: ${errorMessage}`);
            }
          } catch (err) {
            const athlete = athletes.find(a => a.id === parseInt(athleteId));
            errors.push(`${athlete?.name || athleteId}: ${err.message}`);
          }
        }

        if (errors.length > 0 && results.length === 0) {
          throw new Error(errors.join('\n'));
        } else if (errors.length > 0) {
          setError(`Added ${results.length} results. Errors: ${errors.join(', ')}`);
        }
      }

      // If submitted from a template, refresh the pending templates list
      if (activeTemplate) {
        fetchPendingTemplates();
      }
      resetForm();
      fetchResults();
    } catch (err) {
      console.error('Error saving result:', err);
      setError(err.message || 'Failed to save result');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (resultId) => {
    if (!confirm('Are you sure you want to delete this result?')) return;

    try {
      const response = await fetch(`${API_URL}/api/athlete-results/${resultId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });

      if (response.ok) {
        fetchResults();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete result');
      }
    } catch (err) {
      console.error('Error deleting result:', err);
      setError('Failed to delete result');
    }
  };

  const handleEdit = (result) => {
    // Convert MM/DD/YYYY to YYYY-MM-DD for the date input
    const [month, day, year] = result.result_date.split('/');
    const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

    // Convert total seconds to minutes and seconds
    const mins = Math.floor(result.total_time_seconds / 60);
    const secs = Math.round((result.total_time_seconds % 60) * 10) / 10;

    const workoutModeVal = result.workout_mode || 'distance';
    const intervalCountVal = result.interval_count || 1;

    setSelectedAthletes([result.athlete_id.toString()]);
    setResultDate(formattedDate);
    
    // For distance-based workouts with intervals, set per-interval distance (not total)
    if (workoutModeVal === 'distance' && intervalCountVal > 1) {
      // Check if intervals have variable distances
      const hasVariableDistances = result.interval_details && 
        result.interval_details.some(i => i && i.distance_meters != null);
      
      if (hasVariableDistances) {
        // Variable distances - use the first interval's distance as default
        const firstDistance = result.interval_details.find(i => i && i.distance_meters)?.distance_meters;
        setDistanceMeters(firstDistance?.toString() || Math.round(result.distance_meters / intervalCountVal).toString());
      } else {
        // Uniform distances - calculate per-interval distance
        setDistanceMeters(Math.round(result.distance_meters / intervalCountVal).toString());
      }
    } else {
      // Single piece or time-based - use total distance
      setDistanceMeters(result.distance_meters.toString());
    }
    
    setTimeMinutes(mins.toString());
    setTimeSeconds(secs.toString());
    setStrokeRate(result.avg_stroke_rate?.toString() || '');
    setBoatClass(result.boat_class);
    setNotes(result.notes || '');
    setResultType(result.result_type || 'workout');
    setIntervalCount(intervalCountVal);
    
    // For time-based mode, set the fixed time per piece
    if (workoutModeVal === 'time' && intervalCountVal > 1) {
      // Get time per piece from first interval or calculate from total
      const firstIntervalTime = result.interval_details?.[0]?.time_seconds;
      const timePerPiece = firstIntervalTime || Math.round(result.total_time_seconds / intervalCountVal);
      const fixedMins = Math.floor(timePerPiece / 60);
      const fixedSecs = Math.round((timePerPiece % 60) * 10) / 10;
      setFixedTimeMinutes(fixedMins.toString());
      setFixedTimeSeconds(fixedSecs.toString());
    } else if (workoutModeVal === 'time') {
      // Single piece time-based
      setFixedTimeMinutes(mins.toString());
      setFixedTimeSeconds(secs.toString());
    }
    
    // Convert rest seconds to minutes and seconds
    if (result.interval_rest_seconds) {
      const restMins = Math.floor(result.interval_rest_seconds / 60);
      const restSecs = result.interval_rest_seconds % 60;
      setIntervalRestMinutes(restMins > 0 ? restMins.toString() : '');
      setIntervalRestSeconds(restSecs > 0 ? restSecs.toString() : '');
    } else {
      setIntervalRestMinutes('');
      setIntervalRestSeconds('');
    }
    // Load interval details if they exist
    if (result.interval_details && Array.isArray(result.interval_details) && result.interval_details.length > 0) {
      setIntervalDetails(result.interval_details);
      setIntervalSecondsInputs({});
      setIntervalStrokeRateInputs({});
      setIntervalCount(result.interval_details.length);
    } else {
      setIntervalDetails([]);
      setIntervalSecondsInputs({});
      setIntervalStrokeRateInputs({});
    }
    // Load workout mode
    setWorkoutMode(workoutModeVal);
    setEditingResult(result);
    setShowAddForm(true);
  };

  const resetForm = () => {
    setSelectedAthletes([]);
    setAthleteSearch('');
    setResultDate(new Date().toISOString().split('T')[0]);
    setDistanceMeters('');
    setTimeMinutes('');
    setTimeSeconds('');
    setStrokeRate('');
    setBoatClass('');
    setNotes('');
    setResultType('workout');
    setIntervalCount(1);
    setIntervalRestMinutes('');
    setIntervalRestSeconds('');
    setIntervalDetails([]);
    setIntervalSecondsInputs({});
    setIntervalStrokeRateInputs({});
    setWorkoutMode('distance');
    setFixedTimeMinutes('');
    setFixedTimeSeconds('');
    setEditingResult(null);
    setActiveTemplate(null);
    setShowAddForm(false);
    setError('');
    setSubmitting(false);
  };

  // Pre-fill form from a template
  const openFormFromTemplate = (template) => {
    setActiveTemplate(template);
    setResultType(template.result_type || 'test');
    
    // Set workout mode based on piece type
    const pieceType = template.piece_type || 'distance';
    setWorkoutMode(pieceType);
    
    if (pieceType === 'distance') {
      // Distance-based: set distance from template
      setDistanceMeters(template.distance_meters?.toString() || '');
    } else {
      // Time-based: set fixed time from template
      const totalSeconds = template.time_seconds || 0;
      setFixedTimeMinutes(Math.floor(totalSeconds / 60).toString());
      const secs = Math.round((totalSeconds % 60) * 10) / 10;
      setFixedTimeSeconds(secs.toString());
    }
    
    // Set interval configuration
    const intervalType = template.interval_type || 'distance';
    let count = template.interval_count || 1;
    
    // Handle variable intervals
    if (template.intervals && Array.isArray(template.intervals) && template.intervals.length > 0) {
      count = template.intervals.length;
      // Pre-fill interval details structure for variable intervals
      const details = template.intervals.map(i => ({
        time_seconds: null, // Athlete fills this in
        distance_meters: intervalType === 'time' ? null : (i.distance_meters || template.distance_meters),
        stroke_rate: null
      }));
      setIntervalDetails(details);
      setIntervalSecondsInputs({});
      setIntervalStrokeRateInputs({});
    } else if (count > 1) {
      // Uniform intervals
      if (intervalType === 'time' && template.interval_time_seconds) {
        // Time-based intervals (e.g., 5x5')
        const intervalSecs = template.interval_time_seconds;
        setFixedTimeMinutes(Math.floor(intervalSecs / 60).toString());
        const secs = Math.round((intervalSecs % 60) * 10) / 10;
        setFixedTimeSeconds(secs.toString());
        setWorkoutMode('time');
      } else if (template.interval_distance_meters) {
        // Distance-based intervals (e.g., 4x500m)
        setDistanceMeters(template.interval_distance_meters.toString());
      }
    }
    
    setIntervalCount(count);
    
    // Set rest time
    if (template.interval_rest_seconds) {
      setIntervalRestMinutes(Math.floor(template.interval_rest_seconds / 60).toString());
      const restSecs = Math.round((template.interval_rest_seconds % 60) * 10) / 10;
      setIntervalRestSeconds(restSecs.toString());
    }
    
    // Add template info to notes
    setNotes(`Template: ${template.name}`);
    
    setShowAddForm(true);
  };

  const toggleAthleteSelection = (athleteId) => {
    const id = athleteId.toString();
    setSelectedAthletes(prev => 
      prev.includes(id)
        ? prev.filter(a => a !== id)
        : [...prev, id]
    );
  };

  // Update interval details when interval count or workout mode changes
  useEffect(() => {
    if (intervalCount > 1) {
      // Ensure intervalDetails array matches intervalCount
      setIntervalDetails(prev => {
        const currentLength = prev.length;
        if (currentLength < intervalCount) {
          // Add empty intervals - include distance_meters placeholder for time mode
          const newIntervals = Array(intervalCount - currentLength).fill(null).map(() => ({
            time_seconds: null,
            stroke_rate: null,
            distance_meters: workoutMode === 'time' ? null : null
          }));
          return [...prev, ...newIntervals];
        } else if (currentLength > intervalCount) {
          // Remove extra intervals
          return prev.slice(0, intervalCount);
        }
        return prev;
      });
    } else {
      // Reset interval details for single interval
      setIntervalDetails([]);
      setIntervalSecondsInputs({});
      setIntervalStrokeRateInputs({});
    }
  }, [intervalCount, workoutMode]);

  // Update a specific interval's time or rate
  const updateIntervalDetail = (index, field, value) => {
    setIntervalDetails(prev => {
      const updated = [...prev];
      if (!updated[index]) {
        updated[index] = { time_seconds: null, stroke_rate: null };
      }
      updated[index] = {
        ...updated[index],
        [field]: value ? parseFloat(value) : null
      };
      return updated;
    });
  };

  // Filter athletes based on search
  const filteredAthletes = athletes.filter(athlete => 
    athlete.name.toLowerCase().includes(athleteSearch.toLowerCase()) ||
    (athlete.team && athlete.team.toLowerCase().includes(athleteSearch.toLowerCase()))
  );

  const selectAllFiltered = () => {
    const filteredIds = filteredAthletes.map(a => a.id.toString());
    setSelectedAthletes(prev => {
      const newSelection = [...prev];
      filteredIds.forEach(id => {
        if (!newSelection.includes(id)) {
          newSelection.push(id);
        }
      });
      return newSelection;
    });
  };

  const clearAthleteSelection = () => {
    setSelectedAthletes([]);
    setAthleteSearch('');
  };

  // Common distance presets
  const distancePresets = [
    { label: '500m', value: 500 },
    { label: '1000m', value: 1000 },
    { label: '2000m', value: 2000 },
    { label: '5000m', value: 5000 },
    { label: '6000m', value: 6000 },
  ];

  // Format interval details for export
  const formatIntervalDetailsForExport = (result) => {
    if (!result.interval_details || !Array.isArray(result.interval_details)) {
      return '';
    }
    const details = result.interval_details;
    
    return details.map((interval, idx) => {
      const parts = [];
      parts.push(`#${idx + 1}`);
      if (interval.time_seconds) parts.push(formatTime(interval.time_seconds));
      if (interval.distance_meters) parts.push(`${interval.distance_meters}m`);
      if (interval.stroke_rate) parts.push(`${interval.stroke_rate}spm`);
      return parts.join(' ');
    }).join(' | ');
  };

  // Export functions
  const exportToCSV = () => {
    const headers = [
      'Date', 'Athlete', 'Team', 'Boat Class', 'Type', 'Mode',
      'Distance (m)', 'Time', 'Split', 'Stroke Rate',
      'Intervals', 'Rest (sec)', 'Interval Details', 'Notes'
    ];
    const rows = results.map(r => [
      r.result_date,
      r.athlete_name,
      r.athlete_team || '',
      r.boat_class,
      r.result_type || 'workout',
      r.workout_mode || 'distance',
      r.distance_meters || '',
      formatTime(r.total_time_seconds),
      formatTime(r.avg_split_seconds),
      r.avg_stroke_rate || '',
      r.interval_count || 1,
      r.interval_rest_seconds || '',
      formatIntervalDetailsForExport(r),
      r.notes || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'athlete_results.csv';
    link.click();
  };

  // Format interval details for PDF display
  const formatIntervalDetailsForPDF = (result) => {
    if (!result.interval_details || !Array.isArray(result.interval_details) || result.interval_details.length === 0) {
      return '';
    }
    const details = result.interval_details;
    
    return `<div class="interval-details">
      ${details.map((interval, idx) => `
        <span class="interval">
          <strong>#${idx + 1}:</strong> 
          ${interval.time_seconds ? formatTime(interval.time_seconds) : ''}
          ${interval.distance_meters ? ` ${interval.distance_meters}m` : ''}
          ${interval.stroke_rate ? ` (${interval.stroke_rate}spm)` : ''}
        </span>
      `).join(' ')}
    </div>`;
  };

  const exportToPDF = () => {
    // Group results by athlete for summary
    const byAthlete = {};
    results.forEach(r => {
      if (!byAthlete[r.athlete_name]) {
        byAthlete[r.athlete_name] = { results: [], team: r.athlete_team };
      }
      byAthlete[r.athlete_name].results.push(r);
    });

    // Count by type
    const byType = {};
    results.forEach(r => {
      const type = r.result_type || 'workout';
      byType[type] = (byType[type] || 0) + 1;
    });

    let content = `
      <html>
      <head>
        <title>Athlete Results Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
          h1 { color: #333; border-bottom: 2px solid #333; padding-bottom: 10px; }
          h2 { color: #666; margin-top: 30px; }
          table { width: 100%; border-collapse: collapse; margin-top: 15px; }
          th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
          th { background-color: #f5f5f5; font-size: 11px; }
          .summary-box { display: inline-block; padding: 15px; margin: 10px; background: #f9f9f9; border-radius: 8px; }
          .total { font-size: 24px; font-weight: bold; }
          .type-badge { display: inline-block; padding: 2px 6px; border-radius: 4px; font-size: 10px; text-transform: uppercase; }
          .type-test { background: #dbeafe; color: #1e40af; }
          .type-race { background: #fef3c7; color: #92400e; }
          .type-workout { background: #d1fae5; color: #065f46; }
          .interval-details { font-size: 10px; color: #666; margin-top: 4px; }
          .interval { display: inline-block; margin-right: 8px; }
          .mode-badge { font-size: 9px; color: #888; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>Athlete Results Report</h1>
        <p>Generated: ${new Date().toLocaleString()}</p>
        ${filterAthlete ? `<p>Athlete: ${athletes.find(a => a.id === parseInt(filterAthlete))?.name || filterAthlete}</p>` : ''}
        ${filterBoatClass ? `<p>Boat Class: ${filterBoatClass}</p>` : ''}
        
        <div class="summary-box">
          <div>Total Results</div>
          <div class="total">${results.length}</div>
        </div>
        <div class="summary-box">
          <div>Athletes</div>
          <div class="total">${Object.keys(byAthlete).length}</div>
        </div>
        ${Object.entries(byType).map(([type, count]) => `
          <div class="summary-box">
            <div>${type.charAt(0).toUpperCase() + type.slice(1)}s</div>
            <div class="total">${count}</div>
          </div>
        `).join('')}

        <h2>All Results</h2>
        <table>
          <tr>
            <th>Date</th>
            <th>Athlete</th>
            <th>Type</th>
            <th>Boat</th>
            <th>Distance</th>
            <th>Time</th>
            <th>Split</th>
            <th>S/R</th>
            <th>Intervals</th>
          </tr>
          ${results.map(r => `
            <tr>
              <td>${formatDate(r.result_date)}</td>
              <td>${r.athlete_name}${r.athlete_team ? ` (${r.athlete_team})` : ''}</td>
              <td>
                <span class="type-badge type-${r.result_type || 'workout'}">${r.result_type || 'workout'}</span>
                ${r.workout_mode === 'time' ? '<div class="mode-badge">Time-based</div>' : ''}
              </td>
              <td>${r.boat_class}</td>
              <td>${r.distance_meters ? `${r.distance_meters}m` : '-'}</td>
              <td>${formatTime(r.total_time_seconds)}</td>
              <td>${formatTime(r.avg_split_seconds)}</td>
              <td>${r.avg_stroke_rate || '-'}</td>
              <td>
                ${r.interval_count > 1 ? `${r.interval_count}x${r.interval_rest_seconds ? ` (${r.interval_rest_seconds}s rest)` : ''}` : '-'}
                ${formatIntervalDetailsForPDF(r)}
              </td>
            </tr>
          `).join('')}
        </table>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.print();
  };

  // Result selection for report building
  const toggleResultSelection = (resultId) => {
    setSelectedResultIds(prev => 
      prev.includes(resultId) 
        ? prev.filter(id => id !== resultId)
        : [...prev, resultId]
    );
  };

  const selectAllResults = () => {
    if (selectedResultIds.length === results.length) {
      setSelectedResultIds([]);
    } else {
      setSelectedResultIds(results.map(r => r.id));
    }
  };

  const handleBuildReport = async () => {
    if (selectedResultIds.length === 0) {
      setError('Please select at least one result to build a report');
      return;
    }
    
    if (!reportTitle.trim()) {
      setError('Please enter a report title');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/reports`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          title: reportTitle,
          description: reportDescription,
          resultIds: selectedResultIds
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create report');
      }

      setShowReportModal(false);
      setReportTitle('');
      setReportDescription('');
      setSelectedResultIds([]);
      alert('Report created successfully! View it in Reports & Templates.');
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 flex items-center justify-center">
        <p className="text-gray-500">Loading athlete results...</p>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-6 pb-24 sm:pb-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 sm:mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-theme-primary">
            {canViewAll && viewMode === 'all' ? 'Athlete Results' : 'Results'}
          </h2>
          {!canViewAll && (
            <p className="text-sm text-gray-500 mt-1">Track your performance over time</p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {canViewAll && isCoach() && selectedResultIds.length > 0 && (
            <button
              onClick={() => setShowReportModal(true)}
              className="flex items-center gap-1 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Build Report ({selectedResultIds.length})
            </button>
          )}
          {canViewAll && (
            <button
              onClick={exportToCSV}
              className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              CSV
            </button>
          )}
          {canViewAll && (
            <button
              onClick={exportToPDF}
              className="flex items-center gap-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              PDF
            </button>
          )}
          <button
            onClick={() => setShowAddForm(true)}
            className="btn-theme-primary px-4 py-2 rounded-lg"
          >
            + Add Result
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">
          {error}
          <button onClick={() => setError('')} className="float-right font-bold">×</button>
        </div>
      )}

      {/* Results Page Tabs */}
      {(pendingTemplates.length > 0 || submittedTemplates.length > 0) && (
        <div className="flex gap-2 mb-4 border-b border-gray-200 pb-2">
          <button
            onClick={() => setResultsTab('myResults')}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
              resultsTab === 'myResults'
                ? 'bg-theme-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            My Results
          </button>
          <button
            onClick={() => setResultsTab('teamResults')}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              resultsTab === 'teamResults'
                ? 'bg-theme-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Team Results
            {(pendingTemplates.length + submittedTemplates.length) > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                resultsTab === 'teamResults' ? 'bg-white/20' : 'bg-theme-primary text-white'
              }`}>
                {pendingTemplates.length + submittedTemplates.length}
              </span>
            )}
          </button>
        </div>
      )}

      {/* Team Results Tab Content */}
      {resultsTab === 'teamResults' && (pendingTemplates.length > 0 || submittedTemplates.length > 0) && (
        <>
          {/* Pending Templates Section */}
          {pendingTemplates.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
              <h3 className="font-semibold text-yellow-800 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Pending Result Requests ({pendingTemplates.length})
              </h3>
              <div className="space-y-2">
                {pendingTemplates.map(template => {
                  const pieceType = template.piece_type || 'distance';
                  const isTimeBased = pieceType === 'time';
                  let workoutDesc = '';
                  if (template.interval_count > 1) {
                    if (template.intervals && template.intervals.length > 0) {
                      workoutDesc = template.intervals.map(i => 
                        i.time_seconds ? `${Math.floor(i.time_seconds / 60)}'` : `${i.distance_meters}m`
                      ).join('/');
                    } else if (template.interval_time_seconds) {
                      workoutDesc = `${template.interval_count}x${Math.floor(template.interval_time_seconds / 60)}'`;
                    } else if (template.interval_distance_meters) {
                      workoutDesc = `${template.interval_count}x${template.interval_distance_meters}m`;
                    } else {
                      workoutDesc = `${template.interval_count} intervals`;
                    }
                  } else if (isTimeBased) {
                    workoutDesc = `${Math.floor((template.time_seconds || 0) / 60)}'`;
                  } else {
                    workoutDesc = `${template.distance_meters}m`;
                  }
                  
                  return (
                    <div key={template.id} className="bg-white rounded-lg p-3 shadow-sm">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-gray-900">{template.name}</span>
                          <span className="text-sm text-gray-500 ml-2">
                            {workoutDesc}
                            {isTimeBased && ' (timed)'}
                          </span>
                          {template.goal_percent != null && (
                            <span className="block sm:inline text-sm text-indigo-700 sm:ml-2">
                              Goal {Number(template.goal_percent).toFixed(1)}% of 2k
                            </span>
                          )}
                          {template.due_date && (
                            <span className="block sm:inline text-sm text-orange-600 sm:ml-2">
                              Due: {new Date(template.due_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            onClick={() => fetchTeamResults(template.id)}
                            className="px-3 py-2 border border-theme-primary text-theme-primary text-sm font-medium rounded-lg hover:bg-theme-primary/10 flex items-center gap-1"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            Team
                          </button>
                          <button
                            onClick={() => openFormFromTemplate(template)}
                            className="px-4 py-2 bg-theme-primary text-white text-sm font-medium rounded-lg hover:opacity-90"
                          >
                            Submit
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Submitted Templates Section */}
          {submittedTemplates.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
              <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Completed Results ({submittedTemplates.length})
              </h3>
              <div className="space-y-2">
                {submittedTemplates.map(template => {
                  const pieceType = template.piece_type || 'distance';
                  const isTimeBased = pieceType === 'time';
                  let workoutDesc = '';
                  if (template.interval_count > 1) {
                    if (template.intervals && template.intervals.length > 0) {
                      workoutDesc = template.intervals.map(i => 
                        i.time_seconds ? `${Math.floor(i.time_seconds / 60)}'` : `${i.distance_meters}m`
                      ).join('/');
                    } else if (template.interval_time_seconds) {
                      workoutDesc = `${template.interval_count}x${Math.floor(template.interval_time_seconds / 60)}'`;
                    } else if (template.interval_distance_meters) {
                      workoutDesc = `${template.interval_count}x${template.interval_distance_meters}m`;
                    } else {
                      workoutDesc = `${template.interval_count} intervals`;
                    }
                  } else if (isTimeBased) {
                    workoutDesc = `${Math.floor((template.time_seconds || 0) / 60)}'`;
                  } else {
                    workoutDesc = `${template.distance_meters}m`;
                  }
                  
                  return (
                    <div key={template.id} className="bg-white rounded-lg p-3 shadow-sm">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-gray-900">{template.name}</span>
                          <span className="text-sm text-gray-500 ml-2">
                            {workoutDesc}
                            {isTimeBased && ' (timed)'}
                          </span>
                          {template.goal_percent != null && (
                            <span className="block sm:inline text-sm text-indigo-700 sm:ml-2">
                              Goal {Number(template.goal_percent).toFixed(1)}% of 2k
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => fetchTeamResults(template.id)}
                          className="w-full sm:w-auto px-4 py-2 bg-theme-primary text-white text-sm font-medium rounded-lg hover:opacity-90 flex items-center justify-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          View Team Results
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {pendingTemplates.length === 0 && submittedTemplates.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              No team results available
            </div>
          )}
        </>
      )}

      {/* My Results Tab Content */}
      {(resultsTab === 'myResults' || (pendingTemplates.length === 0 && submittedTemplates.length === 0)) && (
        <>
      {/* View Toggle (for coaches/admins) */}
      {canViewAll && (
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setViewMode('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'all'
                ? 'bg-theme-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All Athletes
          </button>
          <button
            onClick={() => setViewMode('my')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'my'
                ? 'bg-theme-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            My Results
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-4">
        {canViewAll && viewMode === 'all' && (
          <select
            value={filterAthlete}
            onChange={(e) => setFilterAthlete(e.target.value)}
            className="border p-3 sm:p-2 rounded-lg bg-white text-gray-800 text-base flex-1"
          >
            <option value="">All Athletes</option>
            {athletes.map((athlete) => (
              <option key={athlete.id} value={athlete.id}>{athlete.name}</option>
            ))}
          </select>
        )}
        <select
          value={filterBoatClass}
          onChange={(e) => setFilterBoatClass(e.target.value)}
          className="border p-3 sm:p-2 rounded-lg bg-white text-gray-800 text-base flex-1 sm:flex-initial sm:w-40"
        >
          <option value="">All Boat Classes</option>
          {boatClassOptions.map((bc) => (
            <option key={bc} value={bc}>{bc}</option>
          ))}
        </select>
        <select
          value={filterResultType}
          onChange={(e) => setFilterResultType(e.target.value)}
          className="border p-3 sm:p-2 rounded-lg bg-white text-gray-800 text-base flex-1 sm:flex-initial sm:w-32"
        >
          <option value="">All Types</option>
          {RESULT_TYPES.map((rt) => (
            <option key={rt.value} value={rt.value}>{rt.label}</option>
          ))}
        </select>
      </div>

      {/* Results Table/Cards */}
      {results.length === 0 ? (
        <div className="bg-gray-50 p-6 rounded-lg text-center text-gray-500">
          <p className="text-lg mb-2">No results found</p>
          <p className="text-sm">Add athlete results to track performance over time.</p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full border-collapse bg-white rounded-lg shadow-sm">
              <thead>
                <tr className="bg-theme-bg-secondary text-theme-primary text-left">
                  {canViewAll && isCoach() && (
                    <th className="p-3 w-10">
                      <input
                        type="checkbox"
                        checked={results.length > 0 && selectedResultIds.length === results.length}
                        onChange={selectAllResults}
                        className="w-4 h-4 rounded border-gray-300"
                        title="Select all"
                      />
                    </th>
                  )}
                  <th className="p-3 font-semibold">Date</th>
                  {viewMode === 'all' && <th className="p-3 font-semibold">Athlete</th>}
                  <th className="p-3 font-semibold">Type</th>
                  <th className="p-3 font-semibold">Boat Class</th>
                  <th className="p-3 font-semibold text-right">Distance</th>
                  <th className="p-3 font-semibold text-right">Time</th>
                  <th className="p-3 font-semibold text-right">Split</th>
                  <th className="p-3 font-semibold text-right">S/R</th>
                  <th className="p-3 font-semibold text-right">% CST</th>
                  {PROGNOSTIC_HEADERS.map((h) => (
                    <th key={h.key} className="p-3 font-semibold text-right">{h.label}</th>
                  ))}
                  <th className="p-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {results.map((result) => {
                  const typeInfo = RESULT_TYPES.find(rt => rt.value === result.result_type) || RESULT_TYPES[0];
                  const isSelected = selectedResultIds.includes(result.id);
                  return (
                    <tr key={result.id} className={`border-t hover:bg-gray-50 ${isSelected ? 'bg-purple-50' : ''}`}>
                      {canViewAll && isCoach() && (
                        <td className="p-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleResultSelection(result.id)}
                            className="w-4 h-4 rounded border-gray-300"
                          />
                        </td>
                      )}
                      <td className="p-3 text-sm">{formatDate(result.result_date)}</td>
                      {viewMode === 'all' && (
                        <td className="p-3">
                          <span className="font-medium">{result.athlete_name}</span>
                          {result.athlete_team && (
                            <span className="text-xs text-gray-500 ml-2">({result.athlete_team})</span>
                          )}
                        </td>
                      )}
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${typeInfo.color}`}>
                          {typeInfo.label}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className="bg-theme-bg-secondary text-theme-primary px-2 py-1 rounded text-sm whitespace-nowrap">
                          {result.boat_class}
                        </span>
                      </td>
                      <td className="p-3 text-right font-mono">
                        {result.workout_mode === 'time' ? (
                          // Time-based workout: show time format and total distance
                          <>
                            {result.interval_count > 1 && result.interval_details?.[0]?.time_seconds ? (
                              <span className="text-purple-600">
                                {result.interval_count}×{Math.floor(result.interval_details[0].time_seconds / 60)}'
                              </span>
                            ) : (
                              <span className="text-purple-600">
                                {Math.floor((result.total_time_seconds || 0) / 60)}'
                              </span>
                            )}
                            <span className="text-xs text-gray-500 block">{result.distance_meters}m total</span>
                          </>
                        ) : result.interval_count > 1 ? (
                          // Distance-based intervals
                          <>
                            {result.interval_details?.some(i => i.distance_meters && i.distance_meters !== result.distance_meters / result.interval_count) ? (
                              <div className="text-xs">
                                {result.interval_details.map((i, idx) => (
                                  <div key={idx}>{i.distance_meters || Math.round(result.distance_meters / result.interval_count)}m</div>
                                ))}
                              </div>
                            ) : (
                              <span>{result.interval_count}×{Math.round(result.distance_meters / result.interval_count)}m</span>
                            )}
                          </>
                        ) : (
                          <span>{result.distance_meters}m</span>
                        )}
                      </td>
                      <td className="p-3 text-right font-mono font-semibold">
                        {formatTime(result.total_time_seconds)}
                        {result.interval_count > 1 && result.interval_rest_seconds && (
                          <span className="text-xs text-gray-400 block">r{result.interval_rest_seconds}s</span>
                        )}
                        {result.interval_details && Array.isArray(result.interval_details) && result.interval_details.length > 0 && (
                          <div className="text-xs text-gray-500 mt-1">
                            {result.interval_details.map((interval, idx) => {
                              const pieceDistance = interval.distance_meters || (result.distance_meters / result.interval_count);
                              const pieceSplit = pieceDistance > 0 ? (interval.time_seconds / pieceDistance) * 500 : null;
                              return (
                                <div key={idx} className="text-left">
                                  #{idx + 1}: {formatTime(interval.time_seconds)}
                                  {interval.distance_meters && interval.distance_meters !== result.distance_meters && (
                                    <span className="text-purple-500"> ({interval.distance_meters}m)</span>
                                  )}
                                  {pieceSplit && <span className="text-blue-500 ml-1">[{formatTime(pieceSplit)}]</span>}
                                  {interval.stroke_rate ? ` @ ${interval.stroke_rate}spm` : ''}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <ResultComparisonBadges
                          comparison={resultComparisons.get(result.id)}
                          className="mt-1 justify-end"
                          compact
                        />
                      </td>
                      <td className="p-3 text-right font-mono text-gray-600">
                        {formatTime(result.avg_split_seconds)}
                      </td>
                      <td className="p-3 text-right font-mono text-gray-600">
                        {result.avg_stroke_rate || '-'}
                      </td>
                      <td className="p-3 text-right font-mono">
                        {(() => {
                          const pct = calculatePercentGMS(result);
                          if (!pct) return <span className="text-gray-400">-</span>;
                          const color = pct >= 95 ? 'text-green-600' : 
                                       pct >= 85 ? 'text-blue-600' : 
                                       pct >= 75 ? 'text-yellow-600' : 'text-gray-600';
                          return <span className={`font-semibold ${color}`}>{pct}%</span>;
                        })()}
                      </td>
                      <PrognosticTableCells result={result} />
                      <td className="p-3">
                        {canMutateResult(result) && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(result)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(result.id)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Delete
                          </button>
                        </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="sm:hidden space-y-3">
            {results.map((result) => {
              const typeInfo = RESULT_TYPES.find(rt => rt.value === result.result_type) || RESULT_TYPES[0];
              return (
              <div key={result.id} className="border rounded-lg p-4 bg-white shadow-sm">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    {viewMode === 'all' && (
                      <>
                        <span className="font-bold text-theme-primary">{result.athlete_name}</span>
                        {result.athlete_team && (
                          <span className="text-xs text-gray-500 ml-2">({result.athlete_team})</span>
                        )}
                      </>
                    )}
                    {viewMode === 'my' && (
                      <span className="text-sm text-gray-600">{formatDate(result.result_date)}</span>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${typeInfo.color}`}>
                      {typeInfo.label}
                    </span>
                    <span className="bg-theme-bg-secondary text-theme-primary px-2 py-1 rounded text-xs whitespace-nowrap">
                      {result.boat_class}
                    </span>
                  </div>
                </div>
                {viewMode === 'all' && (
                  <div className="text-sm text-gray-600 mb-2">{formatDate(result.result_date)}</div>
                )}
                <div className="grid grid-cols-5 gap-1 text-center mb-3 bg-gray-50 rounded-lg p-2">
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                      {result.workout_mode === 'time' ? 'Workout' : 'Dist'}
                    </p>
                    <p className="font-mono font-semibold text-sm">
                      {result.workout_mode === 'time' ? (
                        // Time-based: show time format
                        <span className="text-purple-600">
                          {result.interval_count > 1 && result.interval_details?.[0]?.time_seconds 
                            ? `${result.interval_count}×${Math.floor(result.interval_details[0].time_seconds / 60)}'`
                            : `${Math.floor((result.total_time_seconds || 0) / 60)}'`
                          }
                        </span>
                      ) : result.interval_count > 1 ? (
                        // Distance-based intervals
                        result.interval_details?.some(i => i.distance_meters && i.distance_meters !== Math.round(result.distance_meters / result.interval_count)) ? (
                          <span className="text-purple-600">var</span>
                        ) : (
                          `${result.interval_count}×${Math.round(result.distance_meters / result.interval_count)}m`
                        )
                      ) : `${result.distance_meters}m`}
                    </p>
                    {result.workout_mode === 'time' && (
                      <p className="text-[8px] text-gray-500">{result.distance_meters}m</p>
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">Time</p>
                    <p className="font-mono font-semibold text-sm">{formatTime(result.total_time_seconds)}</p>
                    {result.interval_count > 1 && result.interval_rest_seconds && (
                      <p className="text-[9px] text-gray-400">r{result.interval_rest_seconds}s</p>
                    )}
                    {result.interval_details && Array.isArray(result.interval_details) && result.interval_details.length > 0 && (
                      <div className="text-[9px] text-gray-500 mt-1 space-y-0.5">
                        {result.interval_details.map((interval, idx) => {
                          const pieceDistance = interval.distance_meters || (result.distance_meters / result.interval_count);
                          const pieceSplit = pieceDistance > 0 ? (interval.time_seconds / pieceDistance) * 500 : null;
                          return (
                            <div key={idx}>
                              #{idx + 1}: {formatTime(interval.time_seconds)}
                              {interval.distance_meters && interval.distance_meters !== result.distance_meters && (
                                <span className="text-purple-500"> ({interval.distance_meters}m)</span>
                              )}
                              {pieceSplit && <span className="text-blue-500 ml-1">[{formatTime(pieceSplit)}]</span>}
                              {interval.stroke_rate ? ` @${interval.stroke_rate}` : ''}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">Split</p>
                    <p className="font-mono text-gray-700 text-sm">{formatTime(result.avg_split_seconds)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">S/R</p>
                    <p className="font-mono text-gray-700 text-sm">{result.avg_stroke_rate || '-'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">% CST</p>
                    {(() => {
                      const pct = calculatePercentGMS(result);
                      if (!pct) return <p className="font-mono text-gray-400 text-sm">-</p>;
                      const color = pct >= 95 ? 'text-green-600' : 
                                   pct >= 85 ? 'text-blue-600' : 
                                   pct >= 75 ? 'text-yellow-600' : 'text-gray-600';
                      return <p className={`font-mono font-semibold text-sm ${color}`}>{pct}%</p>;
                    })()}
                  </div>
                </div>
                <ResultComparisonBadges
                  comparison={resultComparisons.get(result.id)}
                  className="mb-2"
                />
                <PrognosticBadges result={result} className="mb-3" />
                {result.notes && (
                  <p className="text-sm text-gray-500 italic mb-3">{result.notes}</p>
                )}
                {canMutateResult(result) && (
                <div className="flex gap-2 pt-3 border-t">
                  <button
                    onClick={() => handleEdit(result)}
                    className="text-blue-600 hover:text-blue-800 px-3 py-3 text-sm border border-blue-200 rounded-lg flex-1 min-h-[44px] active:bg-blue-50 transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(result.id)}
                    className="text-red-600 hover:text-red-800 px-3 py-3 text-sm border border-red-200 rounded-lg flex-1 min-h-[44px] active:bg-red-50 transition-colors"
                  >
                    Delete
                  </button>
                </div>
                )}
              </div>
              );
            })}
          </div>
        </>
      )}
        </>
      )}

      {/* Add/Edit Modal */}
      <MobileModal
        isOpen={showAddForm}
        onClose={resetForm}
        title={editingResult ? 'Edit Result' : 'Add Result'}
        maxWidth="max-w-lg"
      >
        <form onSubmit={handleSubmit}>
          {/* Only show athlete selection for coaches/admins */}
          {canViewAll ? (
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-semibold">
                {editingResult ? 'Athlete *' : 'Athletes * (select multiple for crew boats)'}
              </label>
              {!editingResult && selectedAthletes.length > 0 && (
                <span className="text-sm text-theme-primary font-medium">
                  {selectedAthletes.length} selected
                </span>
              )}
            </div>
            
            {editingResult ? (
              // Single select for editing
              <select
                value={selectedAthletes[0] || ''}
                onChange={(e) => setSelectedAthletes([e.target.value])}
                className="border p-3 rounded w-full text-base"
                required
                disabled
              >
                <option value="">Select athlete...</option>
                {athletes.map((athlete) => (
                  <option key={athlete.id} value={athlete.id}>{athlete.name}</option>
                ))}
              </select>
            ) : (
              // Multi-select for adding with search
              <>
                {/* Selected athletes pills */}
                {selectedAthletes.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2 p-2 bg-gray-50 rounded-lg">
                    {selectedAthletes.map(id => {
                      const athlete = athletes.find(a => a.id.toString() === id);
                      return athlete ? (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1 bg-theme-primary text-white px-2 py-1 rounded-full text-sm"
                        >
                          {athlete.name}
                          <button
                            type="button"
                            onClick={() => toggleAthleteSelection(athlete.id)}
                            className="hover:bg-white/20 rounded-full p-0.5"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}

                {/* Search input */}
                <div className="relative mb-2">
                  <input
                    type="text"
                    value={athleteSearch}
                    onChange={(e) => setAthleteSearch(e.target.value)}
                    placeholder="Search athletes by name or team..."
                    className="border p-3 rounded-lg w-full text-base pl-10"
                  />
                  <svg 
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  {athleteSearch && (
                    <button
                      type="button"
                      onClick={() => setAthleteSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Quick actions */}
                <div className="flex gap-2 mb-2">
                  <button
                    type="button"
                    onClick={selectAllFiltered}
                    className="text-sm px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors"
                  >
                    {athleteSearch ? `Select ${filteredAthletes.length} shown` : 'Select All'}
                  </button>
                  {selectedAthletes.length > 0 && (
                    <button
                      type="button"
                      onClick={clearAthleteSelection}
                      className="text-sm px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 active:bg-red-200 transition-colors"
                    >
                      Clear ({selectedAthletes.length})
                    </button>
                  )}
                </div>

                {/* Athlete list */}
                <div className="border rounded-lg max-h-48 overflow-y-auto bg-white">
                  {filteredAthletes.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      No athletes found matching "{athleteSearch}"
                    </div>
                  ) : (
                    filteredAthletes.map((athlete) => (
                      <label
                        key={athlete.id}
                        className={`flex items-center gap-3 px-3 py-3 cursor-pointer hover:bg-gray-50 active:bg-gray-100 border-b last:border-b-0 min-h-[48px] transition-colors ${
                          selectedAthletes.includes(athlete.id.toString()) ? 'bg-theme-bg-secondary' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedAthletes.includes(athlete.id.toString())}
                          onChange={() => toggleAthleteSelection(athlete.id)}
                          className="w-5 h-5 text-theme-primary rounded"
                        />
                        <span className="flex-1 text-base">{athlete.name}</span>
                        {athlete.team && (
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                            {athlete.team}
                          </span>
                        )}
                      </label>
                    ))
                  )}
                </div>

                {selectedAthletes.length > 1 && (
                  <p className="text-sm text-gray-600 mt-2 bg-blue-50 p-2 rounded-lg">
                    💡 The same result will be added for all <strong>{selectedAthletes.length}</strong> athletes
                  </p>
                )}
              </>
            )}
          </div>
          ) : (
            // Athletes adding their own results - no selection needed
            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                📝 Adding result for <strong>yourself</strong>
              </p>
            </div>
          )}

          {/* Template Info Banner */}
          {activeTemplate && (
            <div className="mb-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm font-medium text-yellow-800">
                📋 Submitting for: <strong>{activeTemplate.name}</strong>
              </p>
              {activeTemplate.description && (
                <p className="text-xs text-yellow-700 mt-1">{activeTemplate.description}</p>
              )}
              {activeTemplate.due_date && (
                <p className="text-xs text-orange-600 mt-1">
                  Due: {new Date(activeTemplate.due_date).toLocaleDateString()}
                </p>
              )}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">Date *</label>
            <input
              type="date"
              value={resultDate}
              onChange={(e) => setResultDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="border p-3 rounded-lg w-full text-base"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">Result Type *</label>
            <div className="flex flex-wrap gap-2">
              {RESULT_TYPES.map((rt) => (
                <button
                  key={rt.value}
                  type="button"
                  onClick={() => setResultType(rt.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    resultType === rt.value
                      ? `${rt.color} ring-2 ring-offset-1 ring-gray-400`
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {rt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Workout Mode Toggle */}
          <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
            <label className="block text-sm font-semibold mb-2 text-purple-800">Workout Mode</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setWorkoutMode('distance')}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  workoutMode === 'distance'
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-purple-700 border border-purple-300 hover:bg-purple-100'
                }`}
              >
                📏 Distance-Based
              </button>
              <button
                type="button"
                onClick={() => setWorkoutMode('time')}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  workoutMode === 'time'
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-purple-700 border border-purple-300 hover:bg-purple-100'
                }`}
              >
                ⏱️ Time-Based
              </button>
            </div>
            <p className="text-xs text-purple-600 mt-2">
              {workoutMode === 'distance' 
                ? '📊 Fixed distance workout - record your time for each piece'
                : '📊 Fixed time workout - record your distance for each piece'
              }
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">Boat Class *</label>
            <select
              value={boatClass}
              onChange={(e) => setBoatClass(e.target.value)}
              className="border p-3 rounded-lg w-full text-base bg-white"
              required
            >
              <option value="">Select boat class...</option>
              {boatClassOptions.map((bc) => (
                <option key={bc} value={bc}>{bc}</option>
              ))}
            </select>
          </div>

          {/* Distance - only for distance-based mode */}
          {workoutMode === 'distance' && (
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2">Distance (meters) *</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {distancePresets.map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    onClick={() => setDistanceMeters(preset.value.toString())}
                    className={`px-3 py-2 rounded-lg text-sm border min-h-[40px] active:scale-95 transition-transform ${
                      distanceMeters === preset.value.toString()
                        ? 'bg-theme-primary text-white border-theme-primary'
                        : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <NumberInput
                value={distanceMeters}
                onChange={(e) => setDistanceMeters(e.target.value)}
                placeholder="e.g., 2000"
                min="1"
                className="border p-3 rounded-lg w-full text-base [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                required
              />
            </div>
          )}

          {/* Time Per Piece - only for time-based mode */}
          {workoutMode === 'time' && (
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2">Time Per Piece *</label>
              <p className="text-xs text-gray-500 mb-2">Fixed time constraint for each interval</p>
              <div className="flex gap-2 items-center">
                <div className="flex-1">
                  <NumberInput
                    value={fixedTimeMinutes}
                    onChange={(e) => setFixedTimeMinutes(e.target.value)}
                    placeholder="Min"
                    min="0"
                    className="border p-3 rounded-lg w-full text-base text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    required
                  />
                  <p className="text-xs text-gray-400 text-center mt-1">min</p>
                </div>
                <span className="text-2xl font-bold text-gray-400 pb-5">:</span>
                <div className="flex-1">
                  <DecimalInput
                    value={fixedTimeSeconds}
                    onChange={(e) => setFixedTimeSeconds(e.target.value)}
                    placeholder="Sec"
                    className="border p-3 rounded-lg w-full text-base text-center"
                  />
                  <p className="text-xs text-gray-400 text-center mt-1">sec</p>
                </div>
              </div>
              {intervalCount > 1 && fixedTimeMinutes && (
                <p className="text-sm text-purple-600 font-medium mt-2 text-center bg-purple-50 py-1 rounded">
                  📊 Recording: {intervalCount}×{formatTime(parseInt(fixedTimeMinutes || 0) * 60 + parseFloat(fixedTimeSeconds || 0))}
                  {(intervalRestMinutes || intervalRestSeconds) && (
                    <span> with {formatTime((parseInt(intervalRestMinutes || 0) * 60) + parseFloat(intervalRestSeconds || 0))} rest</span>
                  )}
                </p>
              )}
            </div>
          )}

          {/* Interval Settings */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg border">
            <label className="block text-sm font-semibold mb-2">Interval Workout</label>
            <div className="flex gap-3 items-center">
              <div className="flex-1">
                <label className="text-xs text-gray-500 mb-1 block">Pieces</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIntervalCount(Math.max(1, intervalCount - 1))}
                    className="w-10 h-10 bg-gray-200 rounded-lg text-lg font-bold hover:bg-gray-300 flex items-center justify-center"
                  >
                    -
                  </button>
                  <NumberInput
                    value={intervalCount}
                    onChange={(e) => setIntervalCount(Math.max(1, parseInt(e.target.value) || 1))}
                    min="1"
                    className="border p-2 rounded-lg w-16 text-center text-base [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <button
                    type="button"
                    onClick={() => setIntervalCount(intervalCount + 1)}
                    className="w-10 h-10 bg-gray-200 rounded-lg text-lg font-bold hover:bg-gray-300 flex items-center justify-center"
                  >
                    +
                  </button>
                </div>
              </div>
              {intervalCount > 1 && (
                <div className="flex-1">
                  <label className="text-xs text-gray-500 mb-1 block">Rest Time</label>
                  <div className="flex gap-1 items-center">
                    <NumberInput
                      value={intervalRestMinutes}
                      onChange={(e) => setIntervalRestMinutes(e.target.value)}
                      placeholder="Min"
                      min="0"
                      className="border p-2 rounded-lg flex-1 text-base text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <span className="text-gray-400">:</span>
                    <DecimalInput
                      value={intervalRestSeconds}
                      onChange={(e) => setIntervalRestSeconds(e.target.value)}
                      placeholder="Sec"
                      className="border p-2 rounded-lg flex-1 text-base text-center"
                    />
                  </div>
                </div>
              )}
            </div>
            {intervalCount > 1 && workoutMode === 'distance' && (
              <p className="text-sm text-gray-600 mt-2 bg-white p-2 rounded border">
                📊 Recording: <strong>{intervalCount}×{distanceMeters || '?'}m</strong>
                {(intervalRestMinutes || intervalRestSeconds) && (
                  <span> with {formatTime((parseInt(intervalRestMinutes || 0) * 60) + parseFloat(intervalRestSeconds || 0))} rest</span>
                )}
              </p>
            )}
          </div>

          {intervalCount > 1 ? (
            /* Individual Interval Times and Rates */
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2">
                Interval {workoutMode === 'time' ? 'Distances' : 'Times'} & Rates *
              </label>
              <div className="space-y-3 p-2 bg-white rounded-lg border">
                {Array.from({ length: intervalCount }).map((_, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg border">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-semibold text-theme-primary">Interval {index + 1}</span>
                      {/* Variable distance toggle for distance mode */}
                      {workoutMode === 'distance' && intervalCount > 1 && (
                        <label className="flex items-center gap-1 text-xs text-gray-500 ml-auto">
                          <input
                            type="checkbox"
                            checked={intervalDetails[index]?.distance_meters != null}
                            onChange={(e) => {
                              if (e.target.checked) {
                                updateIntervalDetail(index, 'distance_meters', distanceMeters || 2000);
                              } else {
                                updateIntervalDetail(index, 'distance_meters', null);
                              }
                            }}
                            className="w-3 h-3"
                          />
                          Different distance
                        </label>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {/* Show distance input for time-based mode */}
                      {workoutMode === 'time' && (
                        <div className="col-span-2 mb-2">
                          <label className="text-xs text-gray-500 mb-1 block">
                            Distance achieved (m)
                          </label>
                          <NumberInput
                            value={intervalDetails[index]?.distance_meters || ''}
                            onChange={(e) => updateIntervalDetail(index, 'distance_meters', e.target.value)}
                            placeholder="e.g., 1250"
                            min="1"
                            className="border p-2 rounded-lg w-full text-base text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            required
                          />
                        </div>
                      )}
                      {/* Time input - only for distance-based mode */}
                      {workoutMode === 'distance' && (<div>
                        <label className="text-xs text-gray-500 mb-1 block">Time (MM:SS.s)</label>
                        <div className="flex gap-1 items-center">
                          <NumberInput
                            value={intervalDetails[index]?.time_seconds ? Math.floor(intervalDetails[index].time_seconds / 60) : ''}
                            onChange={(e) => {
                              const mins = parseInt(e.target.value) || 0;
                              const currentSecs = intervalDetails[index]?.time_seconds || 0;
                              const secs = Math.round((currentSecs % 60) * 10) / 10;
                              updateIntervalDetail(index, 'time_seconds', mins * 60 + secs);
                            }}
                            placeholder="Min"
                            min="0"
                            className="border p-2 rounded-lg w-full text-base text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            required
                          />
                          <span className="text-gray-400">:</span>
                          <input
                            type="text"
                            value={
                              // Use raw input if actively editing, otherwise format from stored value
                              intervalSecondsInputs[index] !== undefined
                                ? intervalSecondsInputs[index]
                                : (() => {
                                    const timeSeconds = intervalDetails[index]?.time_seconds;
                                    if (timeSeconds == null || timeSeconds === 0) return '';
                                    const secs = Math.round((timeSeconds % 60) * 10) / 10;
                                    if (secs === 0) return '';
                                    const secsInt = Math.floor(secs);
                                    const secsDec = secs % 1;
                                    if (secsDec === 0) {
                                      return secsInt < 10 ? `0${secsInt}` : secsInt.toString();
                                    }
                                    const intPart = secsInt < 10 ? `0${secsInt}` : secsInt.toString();
                                    const decPart = secsDec.toFixed(1).substring(1);
                                    return intPart + decPart;
                                  })()
                            }
                            onChange={(e) => {
                              const value = e.target.value;
                              // Allow empty, digits, leading zeros, and decimal points
                              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                // Store raw input for display
                                setIntervalSecondsInputs(prev => ({ ...prev, [index]: value }));
                                
                                // Also update the numeric value (for minutes calculation)
                                const currentTime = intervalDetails[index]?.time_seconds || 0;
                                const mins = Math.floor(currentTime / 60);
                                const secs = parseFloat(value) || 0;
                                if (secs >= 0 && secs < 60) {
                                  updateIntervalDetail(index, 'time_seconds', mins * 60 + secs);
                                }
                              }
                            }}
                            onBlur={() => {
                              // Clear raw input tracking on blur - will use formatted value
                              setIntervalSecondsInputs(prev => {
                                const updated = { ...prev };
                                delete updated[index];
                                return updated;
                              });
                            }}
                            placeholder="Sec"
                            className="border p-2 rounded-lg w-full text-base text-center"
                            required
                          />
                        </div>
                      </div>
                      )}
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Stroke Rate (spm)</label>
                        <input
                          type="text"
                          value={
                            intervalStrokeRateInputs[index] !== undefined
                              ? intervalStrokeRateInputs[index]
                              : (intervalDetails[index]?.stroke_rate || '')
                          }
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                              setIntervalStrokeRateInputs(prev => ({ ...prev, [index]: value }));
                              updateIntervalDetail(index, 'stroke_rate', value);
                            }
                          }}
                          onBlur={() => {
                            setIntervalStrokeRateInputs(prev => {
                              const updated = { ...prev };
                              delete updated[index];
                              return updated;
                            });
                          }}
                          placeholder="e.g., 32"
                          className="border p-2 rounded-lg w-full text-base text-center"
                        />
                      </div>
                    </div>
                    {/* Show split for this interval */}
                    {intervalDetails[index]?.time_seconds > 0 && (workoutMode === 'time' ? intervalDetails[index]?.distance_meters : (intervalDetails[index]?.distance_meters || distanceMeters)) && (
                      <p className="text-xs text-gray-500 mt-2 text-right">
                        Split: {formatTime((intervalDetails[index].time_seconds / (intervalDetails[index]?.distance_meters || distanceMeters)) * 500)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              {/* Total summary for distance-based */}
              {workoutMode === 'distance' && intervalDetails.length === intervalCount && intervalDetails.every(i => i && i.time_seconds > 0) && (
                <div className="text-sm text-theme-primary font-medium mt-2 text-center bg-theme-bg-secondary py-2 rounded">
                  <p>Total Time: {formatTime(intervalDetails.reduce((sum, i) => sum + (i.time_seconds || 0), 0))}</p>
                </div>
              )}
              {/* Total summary for time-based */}
              {workoutMode === 'time' && intervalDetails.length === intervalCount && intervalDetails.every(i => i && i.distance_meters > 0) && (
                <div className="text-sm text-purple-600 font-medium mt-2 text-center bg-purple-50 py-2 rounded space-y-1">
                  <p>Total Distance: {intervalDetails.reduce((sum, i) => sum + (i.distance_meters || 0), 0)}m</p>
                  <p className="text-xs text-gray-600">
                    Total Time: {formatTime((parseInt(fixedTimeMinutes || 0) * 60 + parseFloat(fixedTimeSeconds || 0)) * intervalCount)}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <>
            {/* Single Interval - Total Time (only for distance-based) */}
            {workoutMode === 'distance' && (
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2">Total Time *</label>
              <div className="flex gap-2 items-center">
                <div className="flex-1">
                  <NumberInput
                    value={timeMinutes}
                    onChange={(e) => setTimeMinutes(e.target.value)}
                    placeholder="Min"
                    min="0"
                    className="border p-3 rounded-lg w-full text-base text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    required
                  />
                  <p className="text-xs text-gray-400 text-center mt-1">min</p>
                </div>
                <span className="text-2xl font-bold text-gray-400 pb-5">:</span>
                <div className="flex-1">
                  <DecimalInput
                    value={timeSeconds}
                    onChange={(e) => setTimeSeconds(e.target.value)}
                    placeholder="Sec"
                    className="border p-3 rounded-lg w-full text-base text-center"
                  />
                  <p className="text-xs text-gray-400 text-center mt-1">sec</p>
                </div>
              </div>
              {timeMinutes && (
                <p className="text-sm text-theme-primary font-medium mt-2 text-center bg-theme-bg-secondary py-1 rounded">
                  = {formatTime(parseInt(timeMinutes || 0) * 60 + parseFloat(timeSeconds || 0))}
                </p>
              )}
            </div>
            )}
            
            {/* Single Interval - Distance Achieved (only for time-based) */}
            {workoutMode === 'time' && (
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">Distance Achieved (meters) *</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {distancePresets.map((preset) => (
                    <button
                      key={preset.value}
                      type="button"
                      onClick={() => setDistanceMeters(preset.value.toString())}
                      className={`px-3 py-2 rounded-lg text-sm border min-h-[40px] active:scale-95 transition-transform ${
                        distanceMeters === preset.value.toString()
                          ? 'bg-theme-primary text-white border-theme-primary'
                          : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
                <NumberInput
                  value={distanceMeters}
                  onChange={(e) => setDistanceMeters(e.target.value)}
                  placeholder="e.g., 4500"
                  min="1"
                  className="border p-3 rounded-lg w-full text-base [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  required
                />
              </div>
            )}
            </>
          )}

          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">Avg Stroke Rate (spm)</label>
            <DecimalInput
              value={strokeRate}
              onChange={(e) => setStrokeRate(e.target.value)}
              placeholder="e.g., 32"
              className="border p-3 rounded-lg w-full text-base"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-semibold mb-2">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., Head race, 2k erg test"
              className="border p-3 rounded-lg w-full text-base min-h-[80px] resize-none"
              rows={2}
            />
          </div>

          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded mb-4 text-sm">
              {error}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="submit"
              disabled={submitting}
              className="btn-theme-primary px-4 py-3 rounded-lg flex-1 disabled:opacity-50"
            >
              {submitting 
                ? `Adding ${selectedAthletes.length} result${selectedAthletes.length > 1 ? 's' : ''}...`
                : editingResult 
                  ? 'Save Changes' 
                  : selectedAthletes.length > 1 
                    ? `Add Result for ${selectedAthletes.length} Athletes`
                    : 'Add Result'
              }
            </button>
            <button
              type="button"
              onClick={resetForm}
              disabled={submitting}
              className="bg-gray-500 text-white px-4 py-3 rounded-lg hover:bg-gray-600 flex-1"
            >
              Cancel
            </button>
          </div>
        </form>
      </MobileModal>

      {/* Build Report Modal */}
      <MobileModal
        isOpen={showReportModal}
        onClose={() => {
          setShowReportModal(false);
          setReportTitle('');
          setReportDescription('');
        }}
        title="Build Report"
        maxWidth="max-w-md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Create a report from {selectedResultIds.length} selected result{selectedResultIds.length !== 1 ? 's' : ''}.
          </p>
          
          <div>
            <label className="block text-sm font-semibold mb-1">Report Title *</label>
            <input
              type="text"
              value={reportTitle}
              onChange={(e) => setReportTitle(e.target.value)}
              placeholder="e.g., 2k Test Results - January 2024"
              className="w-full border p-3 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1">Description (optional)</label>
            <textarea
              value={reportDescription}
              onChange={(e) => setReportDescription(e.target.value)}
              placeholder="Add notes or context for this report..."
              rows={3}
              className="w-full border p-3 rounded-lg"
            />
          </div>

          <div className="bg-gray-50 p-3 rounded-lg">
            <h4 className="font-semibold text-sm mb-2">Selected Results Preview:</h4>
            <div className="max-h-40 overflow-y-auto space-y-1">
              {results.filter(r => selectedResultIds.includes(r.id)).map(r => (
                <div key={r.id} className="text-sm text-gray-600 flex justify-between">
                  <span>{r.athlete_name}</span>
                  <span className="font-mono">{formatTime(r.total_time_seconds)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={handleBuildReport}
              className="flex-1 bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 font-semibold"
            >
              Create Report
            </button>
            <button
              onClick={() => {
                setShowReportModal(false);
                setReportTitle('');
                setReportDescription('');
              }}
              className="flex-1 bg-gray-200 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      </MobileModal>

      {/* Team Results Modal */}
      <MobileModal
        isOpen={showTeamResults}
        onClose={() => {
          setShowTeamResults(false);
          setTeamResultsData(null);
          setExpandedTeamResult(null);
        }}
        title={teamResultsData?.template?.name || 'Team Results'}
        maxWidth="max-w-2xl"
      >
        {teamResultsLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-primary"></div>
          </div>
        ) : teamResultsData ? (
          <div className="space-y-4">
            {/* Stats Summary */}
            <div className="grid grid-cols-3 gap-4 bg-gray-50 rounded-xl p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-theme-primary">{teamResultsData.stats.totalSubmissions}</div>
                <div className="text-xs text-gray-500">Submitted</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-theme-primary">{teamResultsData.stats.totalAthletes}</div>
                <div className="text-xs text-gray-500">Athletes</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-theme-primary">{teamResultsData.stats.completionRate}%</div>
                <div className="text-xs text-gray-500">Complete</div>
              </div>
            </div>

            {/* Template Info */}
            <div className="text-center text-sm text-gray-600 border-b pb-3">
              {teamResultsData.template.piece_type === 'time' 
                ? `${Math.floor((teamResultsData.template.time_seconds || 0) / 60)} min`
                : `${teamResultsData.template.distance_meters}m`}
              {teamResultsData.template.interval_count > 1 && 
                ` × ${teamResultsData.template.interval_count} intervals`}
              {teamResultsData.template.goal_percent != null &&
                ` • Goal ${Number(teamResultsData.template.goal_percent).toFixed(1)}% of 2k`}
            </div>

            {/* Results List */}
            {teamResultsData.submissions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                No results submitted yet
              </div>
            ) : (
              <div className="space-y-2">
                {teamResultsData.submissions.map((submission, index) => {
                  const hasIntervals = submission.interval_details && Array.isArray(submission.interval_details) && submission.interval_details.length > 1;
                  const isExpanded = expandedTeamResult === submission.id;
                  
                  return (
                  <div 
                    key={submission.id}
                    className={`rounded-lg border overflow-hidden ${
                      submission.is_own_result 
                        ? 'bg-theme-primary/10 border-theme-primary' 
                        : 'bg-white border-gray-200'
                    }`}
                  >
                    {/* Main Row */}
                    <div 
                      className={`flex items-center gap-3 p-3 ${hasIntervals ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                      onClick={() => hasIntervals && setExpandedTeamResult(isExpanded ? null : submission.id)}
                    >
                      {/* Rank */}
                      <div className="w-10 text-center font-semibold">
                        {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${submission.rank}`}
                      </div>
                      
                      {/* Athlete Info */}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {submission.athlete_name}
                          {submission.is_own_result && (
                            <span className="ml-2 text-xs font-medium text-theme-primary">(You)</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          {submission.boat_class}
                          {submission.athlete_team && ` • ${submission.athlete_team}`}
                          {hasIntervals && (
                            <span className="ml-2 text-theme-primary">
                              {isExpanded ? '▲ Hide splits' : '▼ Show splits'}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Time/Distance */}
                      <div className="text-right">
                        <div className="font-mono font-semibold text-gray-900">
                          {teamResultsData.template.piece_type === 'time'
                            ? `${submission.distance_meters}m`
                            : formatTime(submission.total_time_seconds)}
                        </div>
                        {submission.gms_percentage && (
                          <div className="text-xs font-medium text-theme-primary">
                            {submission.gms_percentage.toFixed(1)}% CST
                          </div>
                        )}
                        <PrognosticBadges result={submission} className="justify-end mt-1" />
                        {submission.avg_split_seconds && teamResultsData.template.piece_type === 'distance' && (
                          <div className="text-xs text-gray-500">
                            {formatTime(submission.avg_split_seconds)} /500m
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Expanded Interval Details */}
                    {isExpanded && hasIntervals && (
                      <div className="border-t bg-gray-50 px-3 py-2">
                        <div className="grid grid-cols-4 gap-2 text-xs font-semibold text-gray-500 mb-1 px-1">
                          <div>Piece</div>
                          <div className="text-right">Time</div>
                          <div className="text-right">Split</div>
                          <div className="text-right">S/R</div>
                        </div>
                        {submission.interval_details.map((interval, idx) => {
                          const pieceDistance = interval.distance_meters || (submission.distance_meters / submission.interval_count);
                          const pieceSplit = pieceDistance > 0 && interval.time_seconds 
                            ? (interval.time_seconds / pieceDistance) * 500 
                            : null;
                          return (
                            <div key={idx} className="grid grid-cols-4 gap-2 text-sm py-1 px-1 border-b border-gray-200 last:border-0">
                              <div className="text-gray-600">#{idx + 1}</div>
                              <div className="text-right font-mono">
                                {interval.time_seconds ? formatTime(interval.time_seconds) : '-'}
                              </div>
                              <div className="text-right font-mono text-gray-600">
                                {pieceSplit ? formatTime(pieceSplit) : '-'}
                              </div>
                              <div className="text-right text-gray-600">
                                {interval.stroke_rate || '-'}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}

            <button
              onClick={() => {
                setShowTeamResults(false);
                setTeamResultsData(null);
                setExpandedTeamResult(null);
              }}
              className="w-full bg-gray-200 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-300 font-medium"
            >
              Close
            </button>
          </div>
        ) : null}
      </MobileModal>
    </div>
  );
}

